import type { FirestoreClientData, SetDataType } from '../interfaces';
import type FirebaseFirestore from 'firebase/firestore';

const BATCH_SIZE = 250;

export class Batch<T> {
  private methods: FirestoreClientData['methods'];

  private db: FirebaseFirestore.Firestore;

  private megaBatch: FirebaseFirestore.WriteBatch[];

  private timestamp: () => FirebaseFirestore.Timestamp;

  private collectionReference: FirebaseFirestore.CollectionReference<T>;

  private batchIndex = 0;

  private totalDocs = 0;

  constructor(
    db: FirebaseFirestore.Firestore,
    collectionReference: FirebaseFirestore.CollectionReference<T>,
    timestamp: () => FirebaseFirestore.Timestamp,
    methods: FirestoreClientData['methods'],
  ) {
    this.db = db;
    this.timestamp = timestamp;
    this.megaBatch = [methods.writeBatch(db)];
    this.collectionReference = collectionReference;
    this.methods = methods;
  }

  private moveIndex(): void {
    this.totalDocs += 1;

    if (this.totalDocs % BATCH_SIZE === 0) {
      this.megaBatch.push(this.methods.writeBatch(this.db));
      this.batchIndex += 1;
    }
  }

  private getReference(id?: string): FirebaseFirestore.DocumentReference {
    if (id) {
      return this.methods.doc(this.collectionReference, `${id}`);
    }

    return this.methods.doc(this.collectionReference);
  }

  public add(docData: Partial<T>): FirebaseFirestore.DocumentReference {
    const docRef = this.getReference();

    this.megaBatch[this.batchIndex].set(docRef, {
      propagate: true, createdAt: new Date(), updatedAt: new Date(), ...docData,
    });

    this.moveIndex();

    return docRef;
  }

  /**
   * Alias for add
   */
  public create(docData: Partial<T>): FirebaseFirestore.DocumentReference {
    return this.add(docData);
  }

  public set(docId: string, docData: Partial<T & SetDataType>): void {
    this.megaBatch[this.batchIndex].set(this.getReference(docId), {
      propagate: true,
      ...docData,
      createdAt: docData.createdAt || this.timestamp(),
      updatedAt: docData.updatedAt || docData.createdAt || this.timestamp(),
    });

    this.moveIndex();
  }

  /**
   * Updates an object in the collection
   * @param  {string} docId
   * @param  {any} updateData
   * @return {void}
   */
  public update(docId: string, docData: Partial<T & SetDataType>): void {
    this.megaBatch[this.batchIndex].update(this.getReference(docId), {
      ...docData,
      propagate: false,
      updatedAt: docData.updatedAt || this.timestamp(),
    });

    this.moveIndex();
  }

  /**
   * Updates an object in the collection
   * @param  {string} docId
   * @param  {any} updateData
   * @return {void}
   */
  public propagate(docId: string, docData: Partial<T & SetDataType>): void {
    this.megaBatch[this.batchIndex].update(this.getReference(docId), {
      ...docData,
      propagate: true,
      updatedAt: docData.updatedAt || this.timestamp(),
    });

    this.moveIndex();
  }

  /**
   * Deletes an object in the collection.
   * Alias for `delete`
   * @param  {string} docId
   * @return {void}
   */
  public remove(docId: string): void {
    this.delete(docId);
  }

  /**
   * Deletes an object in the collection.
   * @param  {string} docId
   * @return {void}
   */
  public delete(docId: string): void {
    this.megaBatch[this.batchIndex].delete(this.getReference(docId));

    this.moveIndex();
  }

  /**
   * Writes the finished batch to the database
   */
  public async commit(): Promise<void> {
    let batchPromises = [];

    this.megaBatch.forEach((batch, index) => {
      // Only commit batch if it has docs in it
      if (index <= this.batchIndex) {
        batchPromises.push(batch.commit());
      }
    });

    await Promise.all(batchPromises);
  }

  public getTotal(): number {
    return this.totalDocs;
  }
}
